import { useState } from "react";
import { Search, Loader2, ShieldCheck, ShieldAlert, Gauge, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { NumberPlate } from "@/components/NumberPlate";
import { toast } from "sonner";

export default function MotLookup() {
  const [vrm, setVrm] = useState("");
  const [loading, setLoading] = useState(false);
  const [dvla, setDvla] = useState(null);
  const [mot, setMot] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!vrm.trim()) return;
    setLoading(true); setDvla(null); setMot(null);
    try {
      const [d, m] = await Promise.all([
        api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`),
        api(`/dvsa/mot-history/${encodeURIComponent(vrm.trim())}`),
      ]);
      setDvla(d); setMot(m);
    } catch (e2) { toast.error(e2.message || "Lookup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="DVLA / MOT Lookup" subtitle="Check MOT & tax status and full MOT history by registration" />
      <form onSubmit={search} className="mb-6 flex gap-2">
        <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} placeholder="AB12 XYZ" data-testid="mot-vrm-input"
          className="flex-1 rounded-lg border border-input bg-plate px-4 py-3 text-center font-mono text-2xl font-bold uppercase tracking-wider text-black outline-none focus:ring-2 focus:ring-brand/40" />
        <button type="submit" disabled={loading} data-testid="mot-search-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-bold text-white disabled:opacity-60">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}Search
        </button>
      </form>

      {dvla && (
        <div className="mb-4 rounded-xl border border-border bg-card p-5" data-testid="dvla-result">
          <div className="mb-4 flex items-center justify-between">
            <NumberPlate vrm={dvla.vrm} size="lg" />
            <div className="text-right"><p className="font-display text-xl font-bold text-midnight">{dvla.make} {dvla.model}</p><p className="text-xs text-muted-foreground">{[dvla.year, dvla.colour, dvla.fuel_type].filter(Boolean).join(" · ")}</p></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-lg p-3 ${dvla.mot_status === "Valid" ? "bg-success/10" : "bg-danger-bg"}`}>
              <div className="flex items-center gap-2">{dvla.mot_status === "Valid" ? <ShieldCheck className="h-5 w-5 text-success" /> : <ShieldAlert className="h-5 w-5 text-danger" />}<span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">MOT {dvla.mot_status}</span></div>
              <p className="mt-1 text-sm font-bold text-midnight">Expires {dvla.mot_expiry || "—"}</p>
            </div>
            <div className={`rounded-lg p-3 ${dvla.tax_status === "Taxed" ? "bg-success/10" : "bg-warning-bg"}`}>
              <div className="flex items-center gap-2"><Calendar className="h-5 w-5 text-brand" /><span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Tax {dvla.tax_status}</span></div>
              <p className="mt-1 text-sm font-bold text-midnight">Due {dvla.tax_expiry || "—"}</p>
            </div>
          </div>
        </div>
      )}

      {mot && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="mot-history">
          <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">MOT History</h3>
          <div className="space-y-3">
            {mot.motTests?.map((t, i) => (
              <div key={i} className="rounded-lg border border-border p-4">
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${t.testResult === "PASSED" ? "bg-success/10 text-success" : "bg-danger-bg text-danger"}`}>{t.testResult}</span>
                  <span className="text-xs text-muted-foreground">{t.completedDate}</span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-sm text-slate-700">
                  <span className="flex items-center gap-1"><Gauge className="h-4 w-4 text-muted-foreground" />{Number(t.odometerValue).toLocaleString()} {t.odometerUnit}</span>
                  <span className="text-xs text-muted-foreground">Expiry {t.expiryDate}</span>
                </div>
                {t.rfrAndComments?.length > 0 && (
                  <ul className="mt-2 space-y-1 border-t border-border pt-2">
                    {t.rfrAndComments.map((r, j) => (
                      <li key={j} className="flex gap-2 text-xs text-warning"><ShieldAlert className="h-3.5 w-3.5 shrink-0" /><span className="text-slate-600">{r.text}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
